// File: frontend/src/components/common/ImageLightbox.tsx

import React, { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  RiCloseLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiExternalLinkLine,
  RiQuillPenLine,
  RiDeleteBin6Line,
  RiDownload2Line,
} from "react-icons/ri";
import { ConfirmModal } from "./ConfirmModal";
import { parseApiError } from "../../utils/parseApiError";
import "./ImageLightbox.css";

/**
 * Optional action bar rendered under the image. Every action is opt-in, so
 * callers that pass nothing (e.g. the in-entry image preview) keep the plain
 * viewer without any buttons.
 */
export interface ImageLightboxActions {
  // Entry id of every image, parallel to `images`. When set, the
  // "entry'ye git" button links to the /entry/{id} permalink of the current image.
  entryIds?: (number | null)[];
  // Enables "yayından kaldır". Called with the index of the current image after
  // the user confirmed. Must throw an Error carrying the backend error code as
  // its message when the removal fails; the lightbox shows the translated text.
  onUnpublish?: (index: number) => Promise<void>;
  // Enables "görseli indir"
  download?: boolean;
}

/**
 * Derive a download file name from the image URL. Opaque ids and legacy
 * upload names both end in a file name with an extension; when the URL has
 * none, the extension is taken from the blob's MIME type.
 */
function buildDownloadFileName(url: string, mimeType: string): string {
  let name = "gorsel";
  try {
    const lastSegment = new URL(url, window.location.origin).pathname.split("/").pop();
    if (lastSegment) name = decodeURIComponent(lastSegment);
  } catch {
    // Keep the default name for an unparsable URL
  }
  if (!/\.[a-z0-9]{2,5}$/i.test(name)) {
    const ext = mimeType.split("/")[1]?.replace("jpeg", "jpg") || "jpg";
    name = `${name}.${ext}`;
  }
  return name;
}

/**
 * Save the image to disk.
 *
 * The <a download> attribute is ignored for cross-origin URLs (the API may run
 * on a different origin than the frontend), so the file is fetched as a blob
 * and saved through an object URL. If the fetch fails (e.g. CORS on a legacy
 * static URL), the image is opened in a new tab so the user can still save it.
 */
async function downloadImage(url: string): Promise<void> {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = buildDownloadFileName(url, blob.type);
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Revoke later: some browsers start the download asynchronously
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
  } catch (err) {
    console.error("Image download failed, opening in a new tab instead:", err);
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

interface ImageLightboxProps {
  // All images belonging to the same entry. A single-element array is valid;
  // in that case the prev/next controls and the counter are not rendered.
  images: string[];
  // Index of the image the user clicked on — the lightbox opens on this one.
  startIndex?: number;
  onClose: () => void;
  // Optional action buttons under the image (see ImageLightboxActions)
  actions?: ImageLightboxActions;
}

/**
 * Full-view image overlay rendered into a portal, replacing the previous
 * "open the raw file in a new tab" behaviour.
 *
 * Follows the same portal pattern as ConfirmModal / ReportModal:
 *   - backdrop click closes
 *   - ESC closes
 *   - arrow keys move between images (only when there is more than one)
 *   - page scrolling is locked while the overlay is open
 *
 * A small "yeni sekmede aç" link is kept at the bottom for users who still
 * want the raw file (e.g. to download or share the direct URL).
 */
export const ImageLightbox: React.FC<ImageLightboxProps> = ({
  images,
  startIndex = 0,
  onClose,
  actions,
}) => {
  const { t } = useTranslation();
  const total = images.length;
  const hasGallery = total > 1;

  // "yayından kaldır" flow state: confirm dialog, request in flight, last error
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [actionError, setActionError] = useState("");
  const [downloading, setDownloading] = useState(false);

  // Clamp the incoming index so a stale/out-of-range value can never render blank.
  const [index, setIndex] = useState(() =>
    Math.min(Math.max(startIndex, 0), Math.max(total - 1, 0))
  );

  // The gallery can shrink while the overlay is open (an image was unpublished).
  // Keep the index inside the new range so the next image slides into place
  // instead of rendering an undefined src.
  useEffect(() => {
    setIndex((i) => Math.min(i, Math.max(total - 1, 0)));
  }, [total]);

  // An error message belongs to the image it was produced for
  useEffect(() => {
    setActionError("");
  }, [index]);

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + total) % total);
  }, [total]);

  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % total);
  }, [total]);

  // Keyboard controls. Re-registered whenever the navigation callbacks change.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // While the unpublish confirm dialog is open, ESC only closes the dialog
      // and the arrow keys must not switch the image being confirmed.
      if (confirmOpen) {
        if (e.key === "Escape" && !unpublishing) setConfirmOpen(false);
        return;
      }
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (!hasGallery) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [hasGallery, goPrev, goNext, onClose, confirmOpen, unpublishing]);

  // Lock background scrolling while the overlay is open, restoring the previous
  // value on unmount so nested/other modals are not affected.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  if (total === 0) return null;

  const currentUrl = images[index];

  // Resolved action availability for the current image
  const currentEntryId = actions?.entryIds?.[index] ?? null;
  const showGoToEntry = currentEntryId !== null;
  const showUnpublish = !!actions?.onUnpublish;
  const showDownload = !!actions?.download;
  const hasActions = showGoToEntry || showUnpublish || showDownload;

  const handleConfirmUnpublish = async () => {
    if (!actions?.onUnpublish) return;
    setUnpublishing(true);
    setActionError("");
    try {
      await actions.onUnpublish(index);
      setConfirmOpen(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      setActionError(parseApiError(msg, t, "lightbox_unpublish_error"));
      setConfirmOpen(false);
    } finally {
      setUnpublishing(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadImage(currentUrl);
    } finally {
      setDownloading(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="image-lightbox-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      {/* Close button — top right of the viewport, not of the image, so it
          stays reachable regardless of the image aspect ratio. */}
      <button
        type="button"
        className="image-lightbox__close"
        onClick={onClose}
        title="kapat"
        aria-label="kapat"
      >
        <RiCloseLine />
      </button>

      {hasGallery && (
        <button
          type="button"
          className="image-lightbox__nav image-lightbox__nav--prev"
          onClick={goPrev}
          title="önceki görsel"
          aria-label="önceki görsel"
        >
          <RiArrowLeftSLine />
        </button>
      )}

      <div
        className={`image-lightbox__stage${hasActions ? " image-lightbox__stage--with-actions" : ""}`}
      >
        <img
          src={currentUrl}
          alt=""
          className="image-lightbox__img"
          // Clicking the image itself must not close the overlay — only the backdrop does.
          onClick={(e) => e.stopPropagation()}
        />

        <div className="image-lightbox__footer">
          {hasGallery && (
            <span className="image-lightbox__counter">
              {index + 1} / {total}
            </span>
          )}
          <a
            href={currentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="image-lightbox__raw-link"
            onClick={(e) => e.stopPropagation()}
          >
            <RiExternalLinkLine /> yeni sekmede aç
          </a>
        </div>

        {/* Action bar — only rendered when the caller enabled at least one action */}
        {hasActions && (
          <div className="image-lightbox__actions" onClick={(e) => e.stopPropagation()}>
            {showGoToEntry && (
              <Link
                to={`/entry/${currentEntryId}`}
                className="image-lightbox__action"
                // The overlay would otherwise stay open on top of the entry page
                onClick={onClose}
              >
                <RiQuillPenLine aria-hidden="true" />
                <span>{t("lightbox_go_to_entry")}</span>
              </Link>
            )}
            {showUnpublish && (
              <button
                type="button"
                className="image-lightbox__action"
                onClick={() => setConfirmOpen(true)}
                disabled={unpublishing}
              >
                <RiDeleteBin6Line aria-hidden="true" />
                <span>{t("lightbox_unpublish")}</span>
              </button>
            )}
            {showDownload && (
              <button
                type="button"
                className="image-lightbox__action"
                onClick={handleDownload}
                disabled={downloading}
              >
                <RiDownload2Line aria-hidden="true" />
                <span>{t("lightbox_download")}</span>
              </button>
            )}
          </div>
        )}

        {actionError && (
          <p className="image-lightbox__error" role="alert">
            {actionError}
          </p>
        )}
      </div>

      {hasGallery && (
        <button
          type="button"
          className="image-lightbox__nav image-lightbox__nav--next"
          onClick={goNext}
          title="sonraki görsel"
          aria-label="sonraki görsel"
        >
          <RiArrowRightSLine />
        </button>
      )}

      {confirmOpen && (
        <ConfirmModal
          message={t("lightbox_unpublish_confirm")}
          confirmLabel={t("lightbox_unpublish_confirm_button")}
          cancelLabel={t("lightbox_unpublish_cancel")}
          onConfirm={handleConfirmUnpublish}
          onCancel={() => setConfirmOpen(false)}
          disabled={unpublishing}
        />
      )}
    </div>,
    document.body
  );
};